/**
 * Ink outlines (ART-BIBLE §2.4) — inverted hull, screen-space constant width.
 *
 * Two details that make the difference between "has outlines" and "is drawn in ink":
 *  1. width is constant in PIXELS, so pushing the camera in never fattens the line;
 *  2. hull normals are position-averaged (`aHull`), so hard-edged box geometry does not
 *     tear the outline open at every corner — the classic inverted-hull artefact.
 * Below 6 px of screen size the hull fades out entirely: distant tokens must not fuzz.
 */

import {
  ShaderMaterial, BackSide, Color, Mesh, InstancedMesh, BufferAttribute,
  type BufferGeometry, type Object3D,
} from 'three';
import { uPxScale } from './toon';
import { INK, inkFor } from './palette';

const _hullDone = new WeakSet<BufferGeometry>();

/**
 * Adds an `aHull` attribute: the position-averaged normal. Does not change topology,
 * so UVs and shading normals are untouched.
 */
export function computeHullNormals(geo: BufferGeometry): BufferGeometry {
  if (_hullDone.has(geo)) return geo;
  const pos = geo.getAttribute('position');
  const nor = geo.getAttribute('normal');
  if (!pos || !nor) return geo;

  const map = new Map<string, [number, number, number]>();
  const key = (i: number) =>
    `${Math.round(pos.getX(i) * 1e4)},${Math.round(pos.getY(i) * 1e4)},${Math.round(pos.getZ(i) * 1e4)}`;

  for (let i = 0; i < pos.count; i++) {
    const k = key(i);
    const acc = map.get(k);
    if (acc) {
      acc[0] += nor.getX(i); acc[1] += nor.getY(i); acc[2] += nor.getZ(i);
    } else {
      map.set(k, [nor.getX(i), nor.getY(i), nor.getZ(i)]);
    }
  }

  const out = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const a = map.get(key(i))!;
    let [x, y, z] = a;
    const l = Math.hypot(x, y, z) || 1;
    x /= l; y /= l; z /= l;
    out[i * 3] = x; out[i * 3 + 1] = y; out[i * 3 + 2] = z;
  }
  geo.setAttribute('aHull', new BufferAttribute(out, 3));
  _hullDone.add(geo);
  return geo;
}

export interface OutlineOpts {
  /** width in screen pixels (ART-BIBLE §2.4 table) */
  widthPx?: number;
  /** body colour the outline should remember */
  body?: number;
  warm?: boolean;
  /** per-instance width multiplier attribute (tokens: [Schwer] draws heavier) */
  instanced?: boolean;
  fadeLo?: number;
  fadeHi?: number;
}

export function outlineMaterial(opts: OutlineOpts = {}): ShaderMaterial {
  const color = opts.body !== undefined ? inkFor(opts.body, opts.warm) : INK;
  const mat = new ShaderMaterial({
    side: BackSide,
    transparent: false,
    uniforms: {
      uColor: { value: new Color(color) },
      uWidthPx: { value: opts.widthPx ?? 2.4 },
      uPxScale,
      uFadeLo: { value: opts.fadeLo ?? 6.0 },
      uFadeHi: { value: opts.fadeHi ?? 14.0 },
    },
    vertexShader: /* glsl */`
      uniform float uWidthPx;
      uniform float uPxScale;
      uniform float uFadeLo;
      uniform float uFadeHi;
      attribute vec3 aHull;
      #ifdef SPH_OUTLINE_INSTANCED
        attribute float aWidth;
      #endif
      varying float vFade;

      void main() {
        vec3 nrm = length( aHull ) > 0.01 ? normalize( aHull ) : normalize( normal );
        #ifdef USE_INSTANCING
          vec4 mv = modelViewMatrix * instanceMatrix * vec4( position, 1.0 );
          vec3 n  = normalize( mat3( modelViewMatrix ) * mat3( instanceMatrix ) * nrm );
        #else
          vec4 mv = modelViewMatrix * vec4( position, 1.0 );
          vec3 n  = normalize( normalMatrix * nrm );
        #endif
        float depth  = max( 0.001, - mv.z );
        float sizePx = 2.0 * length( position ) / max( 1e-4, uPxScale * depth );
        vFade = smoothstep( uFadeLo, uFadeHi, sizePx );
        float w = uWidthPx;
        #ifdef SPH_OUTLINE_INSTANCED
          w *= aWidth;
        #endif
        mv.xyz += n * ( w * uPxScale * depth * vFade );
        gl_Position = projectionMatrix * mv;
      }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor;
      varying float vFade;
      void main() {
        if ( vFade < 0.02 ) discard;
        gl_FragColor = vec4( uColor, 1.0 );
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
      }
    `,
  });
  if (opts.instanced) mat.defines = { SPH_OUTLINE_INSTANCED: '' };
  return mat;
}

/**
 * Attach an ink hull to a mesh. Returns the hull mesh (already parented to the source,
 * so it inherits every transform for free).
 * NEVER call this on neon, glass, sprites or particles — outlines are for objects, not light.
 */
export function inkOutline(mesh: Mesh, opts: OutlineOpts = {}): Mesh {
  computeHullNormals(mesh.geometry);
  const hull = new Mesh(mesh.geometry, outlineMaterial(opts));
  hull.name = mesh.name + ':ink';
  hull.renderOrder = (mesh.renderOrder ?? 0) - 1;
  hull.frustumCulled = mesh.frustumCulled;
  hull.userData.sphInk = true;
  mesh.add(hull);
  return hull;
}

/** Instanced hull that shares the source InstancedMesh's matrices (one extra draw call). */
export function inkOutlineInstanced(src: InstancedMesh, opts: OutlineOpts = {}): InstancedMesh {
  computeHullNormals(src.geometry);
  const mat = outlineMaterial({ ...opts, instanced: true });
  const hull = new InstancedMesh(src.geometry, mat, src.count);
  hull.instanceMatrix = src.instanceMatrix;      // shared buffer: no extra CPU work
  hull.count = src.count;
  hull.frustumCulled = false;
  hull.name = src.name + ':ink';
  hull.renderOrder = -1;
  hull.userData.sphInk = true;
  return hull;
}

/** Walk a prefab and ink every mesh that opted in via userData.ink = widthPx. */
export function inkGroup(root: Object3D, defaultWidth = 2.4): number {
  let n = 0;
  const targets: Mesh[] = [];
  root.traverse((o) => {
    const m = o as Mesh;
    if (!m.isMesh || m.userData.sphInk || m.userData.noInk || m.userData.inked) return;
    targets.push(m);
  });
  for (const m of targets) {
    const w = typeof m.userData.ink === 'number' ? m.userData.ink : defaultWidth;
    if (w <= 0) continue;
    const body = (m.material as { color?: Color }).color?.getHex?.();
    inkOutline(m, { widthPx: w, body, warm: !!m.userData.warmInk });
    n++;
  }
  return n;
}
